/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect } from "react";
import { Case, Default, Switch } from "react-if";
import styled from "styled-components";
import { Spinner, theme } from "@nordcloud/gnui";
import {
  RecalculationStatus,
  useGetCustomerDirtinessQuery,
  useGetRecalculationStatusLazyQuery,
  useTriggerRecalculationMutation,
} from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { showError, showSuccess } from "~/services/toast";
import { getFirstItem } from "~/tools";
import { RecalculationItem } from "./RecalculationItem";
import {
  RecalculationItemCaption,
  RecalculationItemSummary,
} from "./recalculationItem.constants";

export function Recalculation() {
  const { data, loading } = useGetCustomerDirtinessQuery({
    pollInterval: POLL_INTERVAL_MS,
  });

  const [
    getRecalculationStatus,
    { data: recalculation, loading: isRecalculationLoading },
  ] = useGetRecalculationStatusLazyQuery();

  const [recalculate, { data: recalculationResponse }] =
    useTriggerRecalculationMutation({
      onCompleted: () => {
        showSuccess(SUCCESS_TEXT.recalculationSuccess);
        void getRecalculationStatus();
      },
      onError: (err) => {
        const isThrottled = err.message.includes("throttled");
        if (isThrottled) {
          showError(ERROR_TEXT.throttledRecalculation);
        } else {
          showError(ERROR_TEXT.failedRecalculation);
        }
      },
    });

  useEffect(() => {
    if (data?.customerDirtiness.isDirty) {
      void getRecalculationStatus();
    }
  }, [data, getRecalculationStatus]);

  const isRecalculationInProgress =
    getFirstItem(recalculation?.recalculations ?? [])?.status ===
      RecalculationStatus.Running ||
    recalculationResponse?.triggerRecalculation;

  const isCustomerDirty = data?.customerDirtiness.isDirty;

  const showCallToAction = !isRecalculationInProgress && isCustomerDirty;

  return (
    <LoaderWrap loading={loading || isRecalculationLoading} Component={Loader}>
      <Switch>
        <Case condition={isRecalculationInProgress}>
          <RecalculationItem
            caption={RecalculationItemCaption.UPDATE_IN_PROGRESS}
            icon="countdown"
            summary={RecalculationItemSummary.UPDATE_IN_PROGRESS}
          />
        </Case>
        <Case condition={showCallToAction}>
          <RecalculationItem
            caption={RecalculationItemCaption.OUTDATED_DATA}
            icon="refresh"
            summary={RecalculationItemSummary.OUTDATED_DATA}
            onClick={recalculate}
          />
        </Case>
        <Default>
          <RecalculationItem
            caption={RecalculationItemCaption.UP_TO_DATE}
            icon="refreshSuccess"
            summary={RecalculationItemSummary.UP_TO_DATE}
          />
        </Default>
      </Switch>
    </LoaderWrap>
  );
}

function Loader() {
  return (
    <LoaderWrapper>
      <Spinner />
    </LoaderWrapper>
  );
}

const LoaderWrapper = styled.div`
  padding: 0 calc(${theme.spacing.spacing03} + 1px);
`;

const POLL_INTERVAL_MS = 1.5 * 60 * 1000; // 1.5 minutes
